import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { authInitializePassword } from "../../api/auth_initialize_password"
import { useLoading } from "../LoadingContext"

import { Link } from "react-router-dom"
import Card from "@mui/material/Card"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import BasicLayout from "../components/BasicLayout"

const InitializePasswordPage: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const [password, setPassword] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [confirmErrorMessage, setConfirmErrorMessage] = useState<string>("")
  const navigate = useNavigate()

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(e.target.value)
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setConfirmPassword(e.target.value)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  const validate = () => {
    let hasError = false
    const allowedSymbols = "!#$%&()-^[];:,.~|`{+*}?_"
    const invalidCharRegex = new RegExp(`[^a-zA-Z0-9${allowedSymbols.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")}]`)
    const uppercaseRegex = /[A-Z]/
    const lowercaseRegex = /[a-z]/
    const numberRegex = /[0-9]/
    const symbolRegex = new RegExp(`[${allowedSymbols.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")}]`)

    // パスワードのバリデーション
    if (!password || password === "") {
      setErrorMessage("必須項目が入力されていません")
      hasError = true
    } else if (password.length > 32 || password.length < 12) {
      setErrorMessage("入力文字数は12文字から32文字以内です")
      hasError = true
    } else if (invalidCharRegex.test(password)) {
      setErrorMessage("使用できない文字が含まれています")
      hasError = true
    } else if (
      !uppercaseRegex.test(password) ||
      !lowercaseRegex.test(password) ||
      !numberRegex.test(password) ||
      !symbolRegex.test(password)
    ) {
      setErrorMessage("英大文字・英小文字・数字・記号をそれぞれ1文字以上含めてください")
      hasError = true
    } else {
      setErrorMessage("")
    }

    // 確認用パスワードのバリデーション
    if (!confirmPassword || confirmPassword === "") {
      setConfirmErrorMessage("必須項目が入力されていません")
      hasError = true
    } else if (confirmPassword.length > 32 || confirmPassword.length < 12) {
      setConfirmErrorMessage("入力文字数は12文字から32文字以内です")
      hasError = true
    } else {
      setConfirmErrorMessage("")
    }

    // パスワードの一致チェック
    if (!hasError && password !== confirmPassword) {
      setConfirmErrorMessage("パスワードが一致していません")
      hasError = true
    }

    return !hasError
  }

  const handleSubmit = async () => {
    if (validate()) {
      try {
        startLoading()
        await authInitializePassword(password)
        setConfirmErrorMessage("")
        localStorage.setItem("is_initialize", "1")
        navigate("/home") // 認証成功後にダッシュボードへ
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ
        } else if (error.response && error.response.status === 402) {
          setConfirmErrorMessage("パスワード形式に誤りがあります")
        } else {
          setConfirmErrorMessage("サーバーへの接続に失敗しました")
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <BasicLayout>
      <Card>
        <MDBox textAlign="center" pt={6} pb={4}>
          <MDTypography variant="h4" fontWeight="medium" mb={3}>
            初期パスワード設定
          </MDTypography>
        </MDBox>
        <MDBox px={6} pb={4}>
          <MDBox component="form" role="form">
            <MDBox mb={1}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="New Password"
                name="password"
                value={password}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.1)"
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.2)"
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onChange={(e) => {
                  handlePasswordChange(e)
                }}
              />
            </MDBox>
            {errorMessage && (
              <MDBox
                color="#FF0000" // テキスト色
                mb={2} // 下マージン
                fontSize="0.7em" // フォントサイズ
              >
                {errorMessage}
              </MDBox>
            )}
            <MDBox mb={1}>
              <MDInput
                type={showConfirmPassword ? "text" : "password"}
                label="New Password (Re Input)"
                name="newPassword"
                value={confirmPassword}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.1)"
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.2)"
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                        sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onChange={(e) => {
                  handleConfirmPasswordChange(e)
                }}
              />
            </MDBox>
            {confirmErrorMessage && (
              <MDBox
                color="#FF0000" // テキスト色
                mb={2} // 下マージン
                fontSize="0.7em" // フォントサイズ
              >
                {confirmErrorMessage}
              </MDBox>
            )}
            <MDBox mt={4} mb={2}>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  padding: "0.75rem 0",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0"
                  }
                }}
                onClick={() => {
                  handleSubmit()
                }}
              >
                送信
              </MDButton>
            </MDBox>
            <MDBox textAlign="right">
              <MDTypography
                component={Link}
                to="/login"
                variant="button"
                color="info"
                fontWeight="regular"
                sx={{
                  fontSize: "0.875rem",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline"
                  }
                }}
              >
                戻る
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  )
}

export default InitializePasswordPage
